import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowRight,
  CheckCircle,
  Clock,
  Plus,
  ShieldWarning,
  WarningCircle,
} from "@phosphor-icons/react";
import { useTranslations } from "next-intl";
import { Button } from "@navi-soft/ui/button";
import {
  ForumPageChrome,
  ForumPageShell,
} from "@/components/forum-page-chrome";
import { cn } from "@/lib/cn";

export const Route = createFileRoute("/service")({
  component: ServicePage,
});

const TIMELINE = [
  { status: "done" as const, labelKey: "serviceStepDiagnostics" as const, date: "2026-06-15" },
  { status: "done" as const, labelKey: "serviceStepAwaitingParts" as const, date: "2026-06-18" },
  { status: "active" as const, labelKey: "serviceStepInProgress" as const, date: "2026-06-22" },
  { status: "pending" as const, labelKey: "serviceStepClosed" as const, date: "—" },
];

const EXCHANGE_ROWS = [
  { actionKey: "serviceExRequest" as const, resultKey: "serviceExCase" as const },
  { actionKey: "serviceExParts" as const, resultKey: "serviceExDemand" as const },
  { actionKey: "serviceExHull" as const, resultKey: "serviceExComment" as const },
];

const FROM_YARD = [
  "serviceChipStatus",
  "serviceChipParts",
  "serviceChipHistory",
  "serviceChipSchedule",
  "serviceChipCampaigns",
] as const;

/** Landing Service layout — mock until C5 / ASL. */
function ServicePage() {
  const t = useTranslations("app");

  return (
    <ForumPageShell>
      <ForumPageChrome
        eyebrow={t("serviceEyebrow")}
        title={
          <span className="inline-flex flex-wrap items-baseline gap-2">
            {t("navService")}
            <span className="text-ui-nano font-semibold uppercase tracking-wide text-[var(--brand-text-muted)] opacity-60">
              {t("navLater")}
            </span>
          </span>
        }
        intro={t("serviceIntro")}
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] p-5 shadow-subtle">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <span className="block font-heading text-ui-micro font-semibold text-[var(--brand-navy)]">
                {t("serviceCaseId")}
              </span>
              <span className="text-ui-micro text-[var(--brand-text-muted)]">
                {t("serviceCaseHull")}
              </span>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-ui-micro font-medium text-amber-700">
              <ShieldWarning className="size-2.5" weight="bold" />
              {t("serviceWarranty")}
            </span>
          </div>

          <div className="space-y-0">
            {TIMELINE.map((s, i) => (
              <div key={s.labelKey} className="flex items-start gap-3 pb-4 last:pb-0">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      "flex size-6 shrink-0 items-center justify-center rounded-full",
                      s.status === "done"
                        ? "bg-emerald-100"
                        : s.status === "active"
                          ? "bg-[rgb(var(--brand-navy-rgb)/0.1)] ring-2 ring-[rgb(var(--brand-navy-rgb)/0.3)]"
                          : "bg-[var(--brand-bg-section)]",
                    )}
                  >
                    {s.status === "done" ? (
                      <CheckCircle className="size-3.5 text-emerald-600" weight="fill" />
                    ) : s.status === "active" ? (
                      <Clock className="size-3.5 text-[var(--brand-navy)]" weight="bold" />
                    ) : (
                      <WarningCircle
                        className="size-3.5 text-[var(--brand-text-muted)] opacity-40"
                        weight="regular"
                      />
                    )}
                  </div>
                  {i < TIMELINE.length - 1 ? (
                    <div
                      className={cn(
                        "mt-1 h-6 w-px",
                        s.status === "done"
                          ? "bg-emerald-300"
                          : "bg-[var(--brand-border)]",
                      )}
                    />
                  ) : null}
                </div>
                <div className="pt-0.5">
                  <span
                    className={cn(
                      "text-ui-micro font-medium",
                      s.status === "pending"
                        ? "text-[var(--brand-text-muted)] opacity-50"
                        : "text-[var(--brand-navy)]",
                    )}
                  >
                    {t(s.labelKey)}
                  </span>
                  <span className="block text-ui-micro text-[var(--brand-text-muted)]">
                    {s.date}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <Button
            type="button"
            variant="secondary"
            size="md"
            className="mt-4 w-full"
            disabled
          >
            <Plus className="size-3.5" weight="bold" />
            {t("serviceCreateRequest")}
          </Button>
        </section>

        <section className="overflow-hidden rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] shadow-subtle">
          <div className="border-b border-[var(--brand-border)] px-5 py-4">
            <h2 className="font-heading text-ui-micro font-semibold text-[var(--brand-navy)]">
              {t("serviceExchangeTitle")}
            </h2>
          </div>
          <div className="divide-y divide-[var(--brand-border)]">
            {EXCHANGE_ROWS.map((row) => (
              <div
                key={row.actionKey}
                className="flex items-center gap-3 px-5 py-3 text-ui-micro"
              >
                <span className="min-w-0 flex-1 text-[var(--brand-navy)]">
                  {t(row.actionKey)}
                </span>
                <ArrowRight
                  className="size-3 shrink-0 text-[var(--brand-text-muted)]"
                  weight="bold"
                />
                <span className="w-40 shrink-0 text-[var(--brand-text-muted)]">
                  {t(row.resultKey)}
                </span>
              </div>
            ))}
          </div>
          <div className="border-t border-[var(--brand-border)] px-5 py-4">
            <h3 className="mb-3 font-heading text-ui-micro font-semibold text-[var(--brand-navy)]">
              {t("serviceFromYard")}
            </h3>
            <div className="flex flex-wrap gap-2">
              {FROM_YARD.map((key) => (
                <span
                  key={key}
                  className="rounded-lg bg-[var(--brand-bg-section)] px-2.5 py-1 text-ui-micro text-[var(--brand-navy)]"
                >
                  {t(key)}
                </span>
              ))}
            </div>
          </div>
        </section>
      </div>
    </ForumPageShell>
  );
}
